import { translationsEnUS } from "@/modules/i18n/translations/en-US";
import Image, { StaticImageData } from "next/image";
import { FunctionComponent, ReactNode } from "react";
import { FormattedMessage } from "react-intl";
import LinkButton from "../LinkButton/LinkButton";

type MsgKey = keyof typeof translationsEnUS;

type Highlight = {
  translationKey: MsgKey;
  defaultMessage: string;
};

interface RealmProps {
  imageSrc: StaticImageData;
  imageAltText: string;
  isBackgroundColored?: boolean;

  translationKeyTitle: MsgKey;
  translationDefaultTitle: string;

  translationKeyParagraph?: MsgKey; // now optional
  translationDefaultParagraph?: string; // now optional

  translationKeyCTA: MsgKey;
  translationDefaultCTA: string;

  isReversed?: boolean;
  urlCTA?: string;
  displayImage?: boolean;

  highlights?: Highlight[]; // NEW
  children?: ReactNode; // NEW
}

const CheckIcon = ({ className = "" }: { className?: string }) => (
  <svg
    className={className}
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    aria-hidden="true"
  >
    <path
      d="M20 6L9 17l-5-5"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const Realm: FunctionComponent<RealmProps> = ({
  imageSrc,
  imageAltText,
  isBackgroundColored = false,
  translationKeyTitle,
  translationDefaultTitle,
  translationKeyParagraph,
  translationDefaultParagraph,
  translationKeyCTA,
  translationDefaultCTA,
  isReversed,
  urlCTA,
  displayImage = true,
  highlights,
  children,
}: RealmProps) => {
  const fontText = isBackgroundColored ? "text-white" : "text-zinc-900";
  const subText = isBackgroundColored ? "text-white/80" : "text-zinc-600";

  // If no image, reduce padding to avoid “empty” feel.
  const contentPadding = displayImage ? "xl:px-28" : "xl:px-10";

  return (
    <div className="py-4 max-w-[80%] mx-auto">
      <div
        className={`flex items-center flex-col xl:flex-row ${isReversed ? "xl:flex-row-reverse" : ""}`}
      >
        <div
          className={`w-auto ${displayImage ? "xl:w-1/2" : "xl:w-full"} px-0 ${contentPadding}`}
        >
          <p
            className={`text-4xl md:text-5xl py-4 ${fontText} tracking-tight text-center md:text-left`}
          >
            <FormattedMessage
              defaultMessage={translationDefaultTitle}
              id={translationKeyTitle}
            />
          </p>

          {/* Optional paragraph */}
          {translationKeyParagraph && translationDefaultParagraph && (
            <p
              className={`text-xl ${subText} font-light pt-3 text-center md:text-left`}
            >
              <FormattedMessage
                defaultMessage={translationDefaultParagraph}
                id={translationKeyParagraph}
              />
            </p>
          )}

          {/* Optional highlights list */}
          {highlights?.length ? (
            <div className="mt-7">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4">
                {highlights.map((h) => (
                  <div
                    key={String(h.translationKey)}
                    className={[
                      "group rounded-2xl p-4",
                      isBackgroundColored
                        ? "bg-white/5 ring-1 ring-white/10 hover:bg-white/8"
                        : "bg-zinc-50 ring-1 ring-zinc-200/70 hover:bg-white",
                      "transition-all duration-300",
                    ].join(" ")}
                  >
                    <div className="flex items-start gap-3">
                      <span
                        className={[
                          "mt-0.5 inline-flex h-9 w-9 items-center justify-center rounded-xl",
                          isBackgroundColored
                            ? "bg-white/10 ring-1 ring-white/15 text-white"
                            : "bg-white ring-1 ring-zinc-200 text-zinc-900",
                          "shadow-sm",
                        ].join(" ")}
                      >
                        <CheckIcon className="h-5 w-5" />
                      </span>

                      <div className="flex-1">
                        <p
                          className={[
                            "text-sm md:text-base leading-snug",
                            isBackgroundColored
                              ? "text-white/85"
                              : "text-zinc-700",
                          ].join(" ")}
                        >
                          <FormattedMessage
                            defaultMessage={h.defaultMessage}
                            id={h.translationKey}
                          />
                        </p>

                        {/* optional tiny subline if you ever extend Highlight with subtext */}
                        {/* <p className={isBackgroundColored ? "text-white/60 text-xs mt-1" : "text-zinc-500 text-xs mt-1"}>...</p> */}
                      </div>
                    </div>

                    {/* subtle hover sheen */}
                    <div
                      aria-hidden="true"
                      className={[
                        "pointer-events-none mt-3 h-px w-full",
                        isBackgroundColored
                          ? "bg-gradient-to-r from-transparent via-white/15 to-transparent opacity-0 group-hover:opacity-100"
                          : "bg-gradient-to-r from-transparent via-zinc-300/40 to-transparent opacity-0 group-hover:opacity-100",
                        "transition-opacity",
                      ].join(" ")}
                    />
                  </div>
                ))}
              </div>
            </div>
          ) : null}

          {/* Optional custom rich content */}
          {children ? <div className="mt-6">{children}</div> : null}

          <div className="py-6 flex justify-center xl:justify-end">
            <LinkButton
              href={urlCTA || "/"}
              isReversedColor={isBackgroundColored}
            >
              <FormattedMessage
                defaultMessage={translationDefaultCTA}
                id={translationKeyCTA}
              />
            </LinkButton>
          </div>
        </div>

        {displayImage && (
          <div className="w-auto xl:w-1/2 px-0 xl:px-10 pt-10 xl:pt-0">
            <Image alt={imageAltText} src={imageSrc} className="rounded-md" />
          </div>
        )}
      </div>
    </div>
  );
};

export default Realm;
